import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { serviceSchema, type ServiceFormInput, type ServiceFormOutput, type ServiceFormSubmitValues } from '../../schemas/service.schema';
import { Alert, Form, Input, Button, Select, Label, Radio } from '@/shared/components';
import { useFeeReferenceDataQuery } from '../../hooks/useFeeReferenceDataQuery';
import { useServiceFormSubmit } from '../../hooks/useServiceFormSubmit';
import { useVatDerivedCost } from '../../hooks/useVatDerivedCost';
import type { ServiceRecord } from '../../services/smr-admin';
import { ServiceCostFields } from './ServiceCostFields';
import { ServiceScheduleFields } from './ServiceScheduleFields';
import { ServiceInclusionsField } from './ServiceInclusionsField';
import { ConfirmInclusionsDeletionModal } from './ConfirmInclusionsDeletionModal';
import { twoPlaceMoney } from '../../utils/twoPlaceMoney';

interface ServiceFormProps {
    initialValues?: Partial<ServiceRecord>;
    onSubmit: (values: ServiceFormSubmitValues) => Promise<unknown>;
    isLoading?: boolean;
    submitLabel?: string;
    pendingLabel?: string;
    onCancel?: () => void;
    /** Kept for callers/tests to distinguish the plan-tab instances; no longer gates fields. */
    planId?: string;
    /** Distinguishes field ids when an Add instance and an Edit instance are mounted
     *  together (the plan Services tab); default '' preserves today's static ids. */
    idPrefix?: string;
    /** Create flows that cannot set a status: StorePlanServiceRequest takes no status
     *  and createAndAttachService() always writes 'active'. */
    hideStatus?: boolean;
    autoFocusName?: boolean;
    /** Caller-supplied copy for the non-422 failure alert. Defaults to the standalone
     *  Service Edit wording. */
    genericErrorMessage?: string;
}

export function ServiceForm({
    initialValues,
    onSubmit,
    isLoading = false,
    submitLabel = 'Save',
    pendingLabel = 'Saving...',
    onCancel,
    planId,
    idPrefix = '',
    hideStatus = false,
    autoFocusName,
    genericErrorMessage,
}: ServiceFormProps) {
    // planId is informational only now; kept so plan-tab callers and tests can distinguish instances.
    void planId;

    const { data: refData, isLoading: isRefLoading } = useFeeReferenceDataQuery();
    const vatCategories = refData?.vat_categories ?? [];

    const { handleSubmit, control, watch, setValue, setError, formState: { isSubmitting, dirtyFields } } = useForm<ServiceFormInput, unknown, ServiceFormOutput>({
        resolver: zodResolver(serviceSchema),
        mode: 'onBlur',
        defaultValues: {
            name: initialValues?.name ?? '',
            description: initialValues?.description ?? '',
            status: (initialValues?.status as ServiceFormInput['status']) ?? 'active',
            cost_mode: initialValues?.cost_mode ?? 'breakdown',
            vat_category_id: initialValues?.vat_category_id ?? '',
            total_cost_net: twoPlaceMoney(initialValues?.total_cost_net),
            total_cost_gross: twoPlaceMoney(initialValues?.total_cost_gross),
            position: initialValues?.position,
            service_type: initialValues?.service_type ?? '',
            months_from_start: initialValues?.months_from_start,
            mileage_from_start: initialValues?.mileage_from_start,
            items: (initialValues?.items ?? []).map((item) => ({
                item_id: item.id,
                unit_quantity: item.pivot?.unit_quantity ?? 1,
                unit_price_net: twoPlaceMoney(item.pivot?.unit_price_net) ?? '0.00',
            })),
        },
    });

    const isTotalMode = watch('cost_mode') === 'total';

    // Clear the fields the opposite mode hides, so a cost_mode switch never leaves a
    // stale total_cost_net/gross in form state that the (now hidden) editor cannot
    // reach — which would otherwise fail the schema's superRefine on submit.
    useEffect(() => {
        if (!isTotalMode) {
            setValue('total_cost_net', null);
            setValue('total_cost_gross', null);
        }
    }, [isTotalMode, setValue]);

    useVatDerivedCost({
        watch,
        setValue,
        netField:      'total_cost_net',
        grossField:    'total_cost_gross',
        categoryField: 'vat_category_id',
        categories:    vatCategories,
        enabled:       isTotalMode,
    });

    const fieldId = (field: string) => (idPrefix ? `${idPrefix}-${field}` : field);

    const storedInclusionCount = initialValues?.items?.length ?? 0;

    const knownItemNames = useMemo(
        () => Object.fromEntries((initialValues?.items ?? []).map((item) => [item.id, item.name ?? item.id])),
        [initialValues?.items],
    );

    const { submit, genericError, pendingConversion, confirmConversion, cancelConversion } =
        useServiceFormSubmit({
            onSubmit,
            setError,
            existingCostMode: initialValues?.cost_mode,
            inclusionCount: storedInclusionCount,
            genericErrorMessage: genericErrorMessage ?? 'Could not save the service. Try again.',
        });

    // Only send inclusions the user actually edited: an editor mounted before an Items-tab
    // write would otherwise replay its seeded rows and undo that write.
    const submitService = (values: ServiceFormOutput) => {
        const payload: ServiceFormSubmitValues = { ...values };
        if (!dirtyFields.items) {
            delete payload.items;
        }

        return submit(payload);
    };

    const disabled = isLoading || isSubmitting;

    // noValidate: the number inputs carry min/max, whose native constraint UI would
    // block submit before Zod could render the authored messages.
    return (
        <Form noValidate className="space-y-4 max-w-2xl" onSubmit={handleSubmit(submitService)}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                    <Label htmlFor={fieldId('name')}>Name</Label>
                    <Controller
                        name="name"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <Input
                                id={fieldId('name')}
                                placeholder="Service name"
                                autoFocus={autoFocusName}
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                disabled={disabled}
                                error={!!error}
                                hint={error?.message}
                                hintId={fieldId('name-hint')}
                                aria-invalid={!!error}
                                aria-describedby={error ? fieldId('name-hint') : undefined}
                            />
                        )}
                    />
                </div>

                <div className="md:col-span-2">
                    <Label htmlFor={fieldId('description')}>Description <span className="text-mp-muted">(optional)</span></Label>
                    <Controller
                        name="description"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <Input
                                id={fieldId('description')}
                                placeholder="Description"
                                value={field.value ?? ''}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                disabled={disabled}
                                error={!!error}
                                hint={error?.message}
                                hintId={fieldId('description-hint')}
                            />
                        )}
                    />
                </div>

                {!hideStatus && (
                    <div>
                        <Label htmlFor={fieldId('status')}>Status</Label>
                        <Controller
                            name="status"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <Select
                                    id={fieldId('status')}
                                    value={field.value}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    disabled={disabled}
                                    error={!!error}
                                    hint={error?.message}
                                    options={[
                                        { value: 'active', label: 'Active' },
                                        { value: 'retired', label: 'Retired' },
                                    ]}
                                />
                            )}
                        />
                    </div>
                )}

                <div>
                    <Label htmlFor={fieldId('vat_category_id')}>VAT Category</Label>
                    <Controller
                        name="vat_category_id"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <Select
                                id={fieldId('vat_category_id')}
                                placeholder="Select VAT category"
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                disabled={disabled}
                                error={!!error}
                                hint={error?.message}
                                isLoading={isRefLoading}
                                options={vatCategories.map((v) => ({ value: v.id, label: v.name }))}
                            />
                        )}
                    />
                </div>

                <div className="md:col-span-2">
                    <Label id={fieldId('cost_mode_label')}>Cost Mode</Label>
                    <Controller
                        name="cost_mode"
                        control={control}
                        render={({ field }) => (
                            <div role="radiogroup" aria-labelledby={fieldId('cost_mode_label')} className="flex items-center gap-6">
                                <Radio
                                    id={fieldId('cost_mode_total')}
                                    name={fieldId('cost_mode')}
                                    value="total"
                                    label="Total"
                                    checked={field.value === 'total'}
                                    onChange={field.onChange}
                                />
                                <Radio
                                    id={fieldId('cost_mode_breakdown')}
                                    name={fieldId('cost_mode')}
                                    value="breakdown"
                                    label="Breakdown"
                                    checked={field.value === 'breakdown'}
                                    onChange={field.onChange}
                                />
                            </div>
                        )}
                    />
                </div>

                <ServiceCostFields
                    control={control}
                    fieldId={fieldId}
                    disabled={disabled}
                    isTotalMode={isTotalMode}
                    convertedFromTotal={initialValues?.cost_mode === 'total' && !isTotalMode}
                />

                <ServiceScheduleFields control={control} fieldId={fieldId} disabled={disabled} />

                <ServiceInclusionsField
                    control={control}
                    fieldId={fieldId}
                    disabled={disabled}
                    isTotalMode={isTotalMode}
                    knownItemNames={knownItemNames}
                />
            </div>

            {genericError && (
                <Alert variant="error">
                    <span>{genericError}</span>
                </Alert>
            )}

            <div className="flex items-center gap-2">
                <Button size="sm" disabled={disabled}>
                    {disabled ? pendingLabel : submitLabel}
                </Button>
                {onCancel && (
                    <Button size="sm" type="button" variant="outline" onClick={onCancel} disabled={disabled}>
                        Cancel
                    </Button>
                )}
            </div>

            <ConfirmInclusionsDeletionModal
                isOpen={pendingConversion !== null}
                inclusionCount={storedInclusionCount}
                isLoading={isLoading}
                onCancel={cancelConversion}
                onConfirm={confirmConversion}
            />
        </Form>
    );
}
